// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Check, ChevronsUpDown, DollarSign, User, FileText, Clock, Settings, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  convertLocalDateToUTC,
  formatDateToLocalYmd,
  parseLocalYmdToDate,
  storedPaymentDateToFormYmd,
} from '@/utils/dateUtils';
import { formatBRLFromDigits } from '@/utils/brlFormat';
import {
  FINANCEIRO_PREVISAO_MARKER,
  stripFinanceiroPrevisaoMarker,
} from '@/utils/financialOperationalImpact';

interface Client {
  id: string;
  name: string;
  email: string;
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
}

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  payment_confirmed?: boolean;
  is_negotiating?: boolean;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  actual_payment_date?: string;
  payment_notes?: string;
  administrative_process_id?: string | null;
}

interface SmartFinancialEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  editingEntry?: FinancialEntry | null;
  clients: Client[];
  cases: Case[];
  onSave: () => void;
  dialogType?: "receita" | "despesa" | "general";
}

const INSTALLMENT_FREQUENCY_OPTIONS = [
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal (15 dias)" },
  { value: "mensal", label: "Mensal" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
] as const;

/** Data da parcela índice `index` (0 = primeira), a partir de `start` (local). */
function computeInstallmentLocalDate(start: Date, index: number, frequency: string): Date {
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  switch (frequency) {
    case "semanal":
      d.setDate(d.getDate() + index * 7);
      break;
    case "quinzenal":
      d.setDate(d.getDate() + index * 15);
      break;
    case "bimestral":
      d.setMonth(d.getMonth() + index * 2);
      break;
    case "trimestral":
      d.setMonth(d.getMonth() + index * 3);
      break;
    case "semestral":
      d.setMonth(d.getMonth() + index * 6);
      break;
    case "anual":
      d.setFullYear(d.getFullYear() + index);
      break;
    case "mensal":
    default:
      d.setMonth(d.getMonth() + index);
      break;
  }
  return d;
}

const SmartFinancialEntryForm = ({ 
  isOpen, 
  onClose, 
  editingEntry, 
  clients, 
  cases, 
  onSave,
  dialogType = "general"
}: SmartFinancialEntryFormProps) => {
  const { toast } = useToast();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [casePopoverOpen, setCasePopoverOpen] = useState(false);
  const [recentEntries, setRecentEntries] = useState<FinancialEntry[]>([]);
  
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    entry_type: dialogType,
    category: "",
    payment_date: "",
    status: "Pago", // Padrão mais inteligente
    client_id: "",
    case_id: "",
    is_installment: false,
    installments_count: "1",
    installment_frequency: "mensal",
    down_payment: "0",
    is_conditional_payment: false,
    conditional_type: "",
    payment_notes: "",
    /** Com parcelamento: "total" = valor do contrato (divide entre parcelas); "parcela" = valor de cada parcela. */
    amount_input_mode: "total" as "total" | "parcela",
  });

  const [dateType, setDateType] = useState<'specific' | 'forecast' | 'not_informed'>('specific');

  /** Pagamentos já feitos antes de gravar (abate do total em aberto). */
  const [prepaid, setPrepaid] = useState({
    entradaPaga: false,
    entradaPagaEm: "",
    parcelasPagasCount: 0,
    parcelasPagasDatas: [] as string[],
  });

  const resetPrepaid = useCallback(() => {
    setPrepaid({
      entradaPaga: false,
      entradaPagaEm: "",
      parcelasPagasCount: 0,
      parcelasPagasDatas: [],
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      description: "",
      amount: "",
      entry_type: dialogType,
      category: "",
      payment_date: "",
      status: "Pago",
      client_id: "",
      case_id: "",
      is_installment: false,
      installments_count: "1",
      installment_frequency: "mensal",
      down_payment: "0",
      is_conditional_payment: false,
      conditional_type: "",
      payment_notes: "",
      amount_input_mode: "total",
    });
    setDateType('specific');
    setShowAdvanced(false);
    resetPrepaid();
  }, [dialogType, resetPrepaid]);

  // Ao abrir: nova entrada reseta; edição preenche com os dados do lançamento.
  useEffect(() => {
    if (!isOpen) return;

    if (!editingEntry) {
      resetForm();
      return;
    }

    resetPrepaid();

    const paymentDateStr = storedPaymentDateToFormYmd(editingEntry.payment_date);

    const selectStatuses = ["Pago", "Pendente", "Em Negociação", "Vencido", "Condicional", "Cancelado"];
    let status = editingEntry.status;
    if (!selectStatuses.includes(status)) status = "Pendente";

    if (status === "Pendente" && !paymentDateStr) {
      setDateType("forecast");
    } else if (!paymentDateStr) {
      setDateType("specific");
    } else {
      setDateType("specific");
    }

    const rawAmt = Number(editingEntry.amount);
    const amt = Number.isFinite(rawAmt) ? rawAmt : 0;
    const rawDown = editingEntry.down_payment != null ? Number(editingEntry.down_payment) : 0;
    const down = Number.isFinite(rawDown) ? rawDown : 0;

    const et = String(editingEntry.entry_type || "").toLowerCase();
    const entryType = et === "despesa" ? "despesa" : "receita";

    const openAdvanced =
      !!editingEntry.is_installment ||
      !!(editingEntry.payment_notes && String(editingEntry.payment_notes).trim()) ||
      !!editingEntry.is_conditional_payment ||
      !!paymentDateStr ||
      status === "Pendente";
    setShowAdvanced(openAdvanced);

    setFormData({
      description: editingEntry.description ?? "",
      amount: amt.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      entry_type: entryType,
      category: editingEntry.category ?? "",
      payment_date: paymentDateStr,
      status,
      client_id: editingEntry.client_id ?? "",
      case_id: editingEntry.case_id ?? "",
      is_installment: !!editingEntry.is_installment,
      installments_count: String(editingEntry.installments_count ?? 1),
      installment_frequency: editingEntry.installment_frequency || "mensal",
      down_payment: down.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      is_conditional_payment: !!editingEntry.is_conditional_payment,
      conditional_type: editingEntry.conditional_type ?? "",
      payment_notes: editingEntry.payment_notes ?? "",
      amount_input_mode:
        editingEntry.is_installment && (editingEntry.installments_count ?? 1) > 1
          ? "parcela"
          : "total",
    });
  }, [isOpen, editingEntry, resetForm, resetPrepaid]);

  useEffect(() => {
    const n = Math.max(1, parseInt(String(formData.installments_count), 10) || 1);
    setPrepaid((p) => {
      if (p.parcelasPagasCount <= n) return p;
      return {
        ...p,
        parcelasPagasCount: n,
        parcelasPagasDatas: p.parcelasPagasDatas.slice(0, n),
      };
    });
  }, [formData.installments_count]);

  // Categorias inteligentes baseadas no tipo
  const getSmartCategories = (type: string) => {
    if (type === "receita") {
      return [
        "Honorários",
        "Assessoria Jurídica", 
        "Consultoria Jurídica",
        "Consulta",
        "Processo",
        "Audiência",
        "Petição",
        "Recurso",
        "Protocolo",
        "Outros"
      ];
    } else {
      return [
        "Escritório",
        "Cartório", 
        "Transporte",
        "Marketing",
        "Impostos",
        "Seguros",
        "Equipamentos",
        "Aluguel",
        "Contas",
        "Outros"
      ];
    }
  };

  // Buscar entradas recentes para sugestões inteligentes
  useEffect(() => {
    const fetchRecentEntries = async () => {
      try {
        const { data } = await supabase
          .from('financial_entries')
          .select('description, category, client_id, case_id, amount')
          .eq('entry_type', dialogType)
          .order('created_at', { ascending: false })
          .limit(10);
        
        setRecentEntries(data || []);
      } catch (error) {
        console.error('Erro ao buscar entradas recentes:', error);
      }
    };

    if (isOpen) {
      fetchRecentEntries();
    }
  }, [isOpen, dialogType]);

  // Sugestões inteligentes baseadas em entradas recentes
  const getSmartSuggestions = () => {
    const suggestions = recentEntries.reduce((acc, entry) => {
      if (!acc.categories.includes(entry.category)) {
        acc.categories.push(entry.category);
      }
      if (!acc.descriptions.includes(entry.description)) {
        acc.descriptions.push(entry.description);
      }
      return acc;
    }, { categories: [], descriptions: [] });

    return suggestions;
  };

  const suggestions = getSmartSuggestions();

  // Lógica inteligente para data baseada no status
  const handleStatusChange = (status: string) => {
    if (status === "Pendente") {
      setDateType("forecast");
      setFormData((prev) => ({ ...prev, status, payment_date: "" }));
      return;
    }
    if (status === "Pago") {
      setDateType("specific");
      setFormData((prev) => ({
        ...prev,
        status,
        payment_date: prev.payment_date || formatDateToLocalYmd(new Date()),
      }));
      return;
    }
    setDateType("specific");
    setFormData((prev) => ({ ...prev, status }));
  };

  // Calcular resumo financeiro do parcelamento
  const calculateInstallmentSummary = () => {
    const cleanAmount =
      parseFloat(formData.amount.replace(/\./g, "").replace(",", ".")) || 0;
    const installmentsCount = Math.max(
      1,
      parseInt(String(formData.installments_count), 10) || 1
    );
    const downPayment =
      parseFloat(formData.down_payment.replace(/\./g, "").replace(",", ".")) || 0;

    if (!formData.is_installment) {
      return {
        totalAmount: cleanAmount,
        downPayment: 0,
        remainingAmount: cleanAmount,
        installmentsCount: 1,
        installmentValue: cleanAmount,
        alreadyPaidAmount: 0,
        balanceRemaining: cleanAmount,
        prepaidParcelCount: 0,
        pendingParcelCount: 1,
      };
    }

    if (formData.amount_input_mode === "parcela") {
      const installmentValue = cleanAmount;
      const remainingAmount = installmentValue * installmentsCount;
      const totalAmount = downPayment + remainingAmount;
      const prepaidParcelCount = Math.min(
        Math.max(0, prepaid.parcelasPagasCount),
        installmentsCount
      );
      const entradaQuitada = prepaid.entradaPaga ? downPayment : 0;
      const parcelasQuitadas = prepaidParcelCount * installmentValue;
      const alreadyPaidAmount = entradaQuitada + parcelasQuitadas;
      const balanceRemaining = Math.max(0, totalAmount - alreadyPaidAmount);
      return {
        totalAmount,
        downPayment,
        remainingAmount,
        installmentsCount,
        installmentValue,
        alreadyPaidAmount,
        balanceRemaining,
        prepaidParcelCount,
        pendingParcelCount: installmentsCount - prepaidParcelCount,
      };
    }

    const totalAmount = cleanAmount;
    const remainingAmount = Math.max(0, totalAmount - downPayment);
    const installmentValue =
      installmentsCount > 0 ? remainingAmount / installmentsCount : 0;
    const prepaidParcelCount = Math.min(
      Math.max(0, prepaid.parcelasPagasCount),
      installmentsCount
    );
    const entradaQuitada = prepaid.entradaPaga ? downPayment : 0;
    const parcelasQuitadas = prepaidParcelCount * installmentValue;
    const alreadyPaidAmount = entradaQuitada + parcelasQuitadas;
    const balanceRemaining = Math.max(0, totalAmount - alreadyPaidAmount);
    return {
      totalAmount,
      downPayment,
      remainingAmount,
      installmentsCount,
      installmentValue,
      alreadyPaidAmount,
      balanceRemaining,
      prepaidParcelCount,
      pendingParcelCount: installmentsCount - prepaidParcelCount,
    };
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const summary = calculateInstallmentSummary();
      const amountForDbRow = formData.is_installment
        ? summary.installmentValue
        : summary.totalAmount;

      const resolvePaymentNotesForOperational = (raw: string) => {
        let pn = String(raw || "").trim();
        if (dateType === "forecast" && formData.status === "Pendente") {
          if (!pn.includes(FINANCEIRO_PREVISAO_MARKER)) {
            pn = pn ? `${pn} ${FINANCEIRO_PREVISAO_MARKER}` : FINANCEIRO_PREVISAO_MARKER;
          }
        } else {
          pn = stripFinanceiroPrevisaoMarker(pn);
        }
        return pn || null;
      };

      const paymentNotesResolved = resolvePaymentNotesForOperational(
        formData.payment_notes || ""
      );

      const dataToSave = {
        description: formData.description,
        amount: amountForDbRow,
        entry_type: formData.entry_type,
        category: formData.category,
        payment_date: formData.payment_date ? convertLocalDateToUTC(formData.payment_date) : null,
        status: formData.status,
        client_id: formData.client_id === "none" ? null : formData.client_id || null,
        case_id: formData.case_id === "none" ? null : formData.case_id || null,
        is_installment: formData.is_installment,
        installments_count: formData.is_installment ? parseInt(formData.installments_count) : 1,
        installment_frequency: formData.installment_frequency,
        down_payment:
          parseFloat(formData.down_payment.replace(/\./g, "").replace(",", ".")) || 0,
        is_conditional_payment: formData.is_conditional_payment,
        conditional_type: formData.conditional_type || null,
        payment_notes: paymentNotesResolved,
        user_id: user.id
      };

      if (editingEntry) {
        const updatePayload = { ...dataToSave };
        if (editingEntry.administrative_process_id) {
          updatePayload.administrative_process_id = editingEntry.administrative_process_id;
        }
        const { error } = await supabase
          .from('financial_entries')
          .update(updatePayload)
          .eq('id', editingEntry.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Entrada atualizada com sucesso",
        });
      } else {
        const installmentsCount = formData.is_installment
          ? Math.max(1, parseInt(String(formData.installments_count), 10) || 1)
          : 1;

        if (formData.is_installment && installmentsCount > 1) {
          const installmentAmount = summary.installmentValue;
          const downPayment = summary.downPayment;
          const freq = formData.installment_frequency || "mensal";
          const prepaidCount = summary.prepaidParcelCount;

          if (prepaid.entradaPaga && downPayment > 0 && !prepaid.entradaPagaEm) {
            toast({
              title: "Data da entrada",
              description: "Informe a data em que a entrada foi recebida.",
              variant: "destructive",
            });
            return;
          }
          for (let j = 0; j < prepaidCount; j++) {
            if (!prepaid.parcelasPagasDatas[j]) {
              toast({
                title: "Parcelas pagas",
                description: `Informe a data do pagamento da ${j + 1}ª parcela já quitada.`,
                variant: "destructive",
              });
              return;
            }
          }

          const today = new Date();
          const startLocal = formData.payment_date
            ? parseLocalYmdToDate(formData.payment_date) ??
              new Date(today.getFullYear(), today.getMonth(), today.getDate())
            : new Date(today.getFullYear(), today.getMonth(), today.getDate());

          const baseNotes = paymentNotesResolved?.trim() || "";
          let entradaNote = "";
          if (prepaid.entradaPaga && downPayment > 0 && prepaid.entradaPagaEm) {
            const de = parseLocalYmdToDate(prepaid.entradaPagaEm);
            const label = de
              ? format(de, "dd/MM/yyyy", { locale: ptBR })
              : prepaid.entradaPagaEm;
            entradaNote = `Entrada de ${downPayment.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} recebida em ${label}.`;
          }
          const row0Notes = [entradaNote, baseNotes].filter(Boolean).join(" ").trim() || null;

          const rows = [];
          for (let i = 0; i < installmentsCount; i++) {
            const payLocal = computeInstallmentLocalDate(startLocal, i, freq);
            const scheduledYmd = formatDateToLocalYmd(payLocal);
            const isPrepaidParcel = i < prepaidCount;
            const paidYmd = isPrepaidParcel
              ? prepaid.parcelasPagasDatas[i]
              : null;
            const ymd = isPrepaidParcel && paidYmd ? paidYmd : scheduledYmd;
            const paymentDateIso = ymd ? convertLocalDateToUTC(ymd) : null;
            const isPaidRow = isPrepaidParcel;

            const pd = parseLocalYmdToDate(paidYmd || "");
            const parcelaPagaLabel =
              isPrepaidParcel && pd
                ? `Parcela ${i + 1}/${installmentsCount} paga em ${format(pd, "dd/MM/yyyy", { locale: ptBR })}.`
                : "";

            rows.push({
              description: `${formData.description} (${i + 1}/${installmentsCount})`,
              amount: installmentAmount,
              entry_type: formData.entry_type,
              category: formData.category,
              payment_date: paymentDateIso,
              status: isPaidRow ? "Pago" : "Pendente",
              actual_payment_date: isPaidRow ? paymentDateIso : null,
              client_id: formData.client_id === "none" ? null : formData.client_id || null,
              case_id: formData.case_id === "none" ? null : formData.case_id || null,
              is_installment: true,
              installments_count: installmentsCount,
              installment_frequency: freq,
              down_payment: i === 0 ? downPayment : 0,
              is_conditional_payment: formData.is_conditional_payment,
              conditional_type: formData.conditional_type || null,
              payment_notes:
                i === 0
                  ? [row0Notes, parcelaPagaLabel].filter(Boolean).join(" ").trim() || null
                  : parcelaPagaLabel || (baseNotes || null),
              user_id: user.id,
            });
          }

          const { error } = await supabase.from("financial_entries").insert(rows);
          if (error) throw error;

          toast({
            title: "Sucesso",
            description: `${installmentsCount} parcelas criadas com datas previstas`,
          });
        } else {
          const { error } = await supabase.from("financial_entries").insert([dataToSave]);
          if (error) throw error;

          toast({
            title: "Sucesso",
            description: "Entrada criada com sucesso",
          });
        }
      }

      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar entrada:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao salvar entrada",
        variant: "destructive",
      });
    }
  };

  const selectedClient = clients.find(c => c.id === formData.client_id);
  const selectedCase = cases.find(c => c.id === formData.case_id);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          {/* Monograma AS como marca d'água */}
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-center gap-4 text-left">
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt="Allana Sthéfany"
              className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
            />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">
                {(editingEntry?.entry_type || dialogType) === 'despesa' ? 'Despesa' : 'Receita'}
              </p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                {editingEntry ? 'Editar lançamento' : `Nova ${dialogType === 'despesa' ? 'despesa' : 'receita'}`}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Campos Essenciais - Sempre Visíveis */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-semibold text-gray-800">
                  Descrição *
                </Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Honorários - Cliente X"
                  required
                  className="w-full"
                />
                {/* Sugestões inteligentes */}
                {suggestions.descriptions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {suggestions.descriptions.slice(0, 3).map((desc, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className="text-xs cursor-pointer hover:bg-[#c4ab83]/20"
                        onClick={() => setFormData({ ...formData, description: desc })}
                      >
                        {desc.length > 20 ? `${desc.substring(0, 20)}...` : desc}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount" className="text-sm font-semibold text-gray-800">
                  Valor *
                </Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  {!formData.is_installment
                    ? "Valor único. Para parcelar, escolha «Valor de cada parcela» (ativa parcelamento) ou marque «É parcelado» nas opções avançadas."
                    : formData.amount_input_mode === "total"
                      ? "Valor total do contrato (entrada + parcelas). O sistema divide o restante pelas parcelas."
                      : "Valor de cada parcela. Total = entrada + (parcelas × este valor)."}
                </p>
                <Input
                  id="amount"
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      amount: formatBRLFromDigits(e.target.value),
                    });
                  }}
                  placeholder="0,00"
                  required
                  className="w-full"
                />
                <RadioGroup
                  value={formData.amount_input_mode}
                  onValueChange={(v) => {
                    const mode = v as "total" | "parcela";
                    setFormData((prev) => {
                      if (mode === "parcela") {
                        const n = parseInt(String(prev.installments_count), 10);
                        const installments_count =
                          Number.isFinite(n) && n >= 2 ? String(n) : "2";
                        return {
                          ...prev,
                          amount_input_mode: mode,
                          is_installment: true,
                          installments_count,
                        };
                      }
                      return { ...prev, amount_input_mode: mode };
                    });
                    if (mode === "parcela") setShowAdvanced(true);
                  }}
                  className="flex flex-col gap-2 mt-1"
                >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="total" id="amount-mode-total" />
                      <Label
                        htmlFor="amount-mode-total"
                        className="text-xs font-normal cursor-pointer leading-snug"
                      >
                        Valor total (divide entre as parcelas)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="parcela" id="amount-mode-parcela" />
                      <Label
                        htmlFor="amount-mode-parcela"
                        className="text-xs font-normal cursor-pointer leading-snug"
                      >
                        Valor de cada parcela
                      </Label>
                    </div>
                  </RadioGroup>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-semibold text-gray-800">
                  Categoria *
                </Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {getSmartCategories(formData.entry_type).map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Sugestões de categorias */}
                {suggestions.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {suggestions.categories.slice(0, 3).map((category, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-[#c4ab83]/20"
                        onClick={() => setFormData({ ...formData, category })}
                      >
                        {category}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className="text-sm font-semibold text-gray-800">
                  Status *
                </Label>
                <Select 
                  value={formData.status} 
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pago">Pago</SelectItem>
                    <SelectItem value="Pendente">Pendente</SelectItem>
                    <SelectItem value="Em Negociação">Em Negociação</SelectItem>
                    <SelectItem value="Vencido">Vencido</SelectItem>
                    <SelectItem value="Condicional">Condicional</SelectItem>
                    <SelectItem value="Cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cliente com Combobox */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Cliente
                </Label>
                <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientPopoverOpen}
                      className="w-full justify-between rounded-lg border-slate-200 font-normal text-slate-700 shadow-none hover:bg-slate-50"
                    >
                      {selectedClient ? selectedClient.name : "Selecione um cliente"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar cliente..." />
                      <CommandList>
                        <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem value="none" onSelect={() => { setFormData({ ...formData, client_id: "" }); setClientPopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${formData.client_id === "" ? "opacity-100" : "opacity-0"}`} />
                            Sem cliente
                          </CommandItem>
                          {clients.map((client) => (
                            <CommandItem key={client.id} value={client.name} onSelect={() => { setFormData({ ...formData, client_id: client.id }); setClientPopoverOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.client_id === client.id ? "opacity-100" : "opacity-0"}`} />
                              {client.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Processo com Combobox */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Processo
                </Label>
                <Popover open={casePopoverOpen} onOpenChange={setCasePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={casePopoverOpen}
                      className="w-full justify-between rounded-lg border-slate-200 font-normal text-slate-700 shadow-none hover:bg-slate-50"
                    >
                      {selectedCase ? selectedCase.title : "Selecione um processo"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar processo..." />
                      <CommandList>
                        <CommandEmpty>Nenhum processo encontrado.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem value="none" onSelect={() => { setFormData({ ...formData, case_id: "" }); setCasePopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${formData.case_id === "" ? "opacity-100" : "opacity-0"}`} />
                            Sem processo
                          </CommandItem>
                          {cases.map((case_) => (
                            <CommandItem key={case_.id} value={case_.title} onSelect={() => { setFormData({ ...formData, case_id: case_.id }); setCasePopoverOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.case_id === case_.id ? "opacity-100" : "opacity-0"}`} />
                              {case_.title}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Botão para Opções Avançadas */}
          <div className="flex items-center justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 rounded-xl border-slate-200 text-slate-600 shadow-none hover:bg-slate-50"
            >
              <Settings className="w-4 h-4" />
              {showAdvanced ? 'Ocultar opções avançadas' : 'Mostrar opções avançadas'}
            </Button>
          </div>

          {/* Opções Avançadas - Condicionais */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Opções Avançadas
              </h3>
              
              <div className="flex flex-col gap-6">
                <div className="space-y-2 w-full min-w-0">
                  <Label htmlFor="payment_date" className="text-sm font-semibold text-gray-800">
                    Data de Recebimento
                    {dateType === 'forecast' && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        Prevista
                      </Badge>
                    )}
                    {dateType === 'not_informed' && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        Não informada
                      </Badge>
                    )}
                  </Label>
                  
                  {dateType === 'forecast' ? (
                    <div className="space-y-2 w-full min-w-0">
                      <div className="flex flex-col sm:flex-row gap-2 w-full min-w-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDateType('specific')}
                          className="w-full sm:flex-1 sm:min-w-0 shrink-0 justify-center whitespace-normal text-center h-auto min-h-9 py-2"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
                          Definir data específica
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDateType('not_informed')}
                          className="w-full sm:flex-1 sm:min-w-0 shrink-0 justify-center whitespace-normal text-center h-auto min-h-9 py-2"
                        >
                          Não informada
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        💡 Data prevista - será definida quando o pagamento for confirmado
                      </p>
                    </div>
                  ) : dateType === 'not_informed' ? (
                    <div className="space-y-2 w-full min-w-0">
                      <div className="flex flex-col sm:flex-row gap-2 w-full min-w-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDateType('specific')}
                          className="w-full sm:flex-1 sm:min-w-0 shrink-0 justify-center whitespace-normal text-center h-auto min-h-9 py-2"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
                          Definir data específica
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDateType('forecast')}
                          className="w-full sm:flex-1 sm:min-w-0 shrink-0 justify-center whitespace-normal text-center h-auto min-h-9 py-2"
                        >
                          Data prevista
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        ⚠️ Data não informada - será solicitada posteriormente
                      </p>
                    </div>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {(() => {
                            if (!formData.payment_date) return "Selecionar data";
                            const d = parseLocalYmdToDate(formData.payment_date);
                            return d
                              ? format(d, "dd/MM/yyyy", { locale: ptBR })
                              : formData.payment_date;
                          })()}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={parseLocalYmdToDate(formData.payment_date) ?? undefined}
                          onSelect={(date) =>
                            setFormData({
                              ...formData,
                              payment_date: date ? formatDateToLocalYmd(date) : "",
                            })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                <div className="space-y-2 w-full min-w-0 rounded-lg border border-gray-200/80 bg-white p-3 sm:p-4">
                  <Label className="text-sm font-semibold text-gray-800">Parcelamento</Label>
                  <div className="flex items-start gap-3 pt-1">
                    <Checkbox
                      id="is_installment"
                      className="mt-0.5 shrink-0"
                      checked={formData.is_installment}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          is_installment: !!checked,
                          ...(checked ? {} : { amount_input_mode: "total" as const }),
                        })
                      }
                    />
                    <Label htmlFor="is_installment" className="text-sm font-normal leading-snug cursor-pointer">
                      É parcelado
                    </Label>
                  </div>
                </div>
              </div>

              {formData.is_installment && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="installments_count" className="text-sm font-semibold text-gray-800">
                        Número de Parcelas
                      </Label>
                      <Input
                        id="installments_count"
                        type="number"
                        min="1"
                        max="60"
                        value={formData.installments_count}
                        onChange={(e) => setFormData({ ...formData, installments_count: e.target.value })}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="installment_frequency" className="text-sm font-semibold text-gray-800">
                        Frequência das parcelas
                      </Label>
                      <Select
                        value={formData.installment_frequency}
                        onValueChange={(value) =>
                          setFormData({ ...formData, installment_frequency: value })
                        }
                      >
                        <SelectTrigger id="installment_frequency" className="w-full">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {INSTALLMENT_FREQUENCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="down_payment" className="text-sm font-semibold text-gray-800">
                      Valor de Entrada
                    </Label>
                    <Input
                      id="down_payment"
                      type="text"
                      value={formData.down_payment}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          down_payment: formatBRLFromDigits(e.target.value),
                        });
                      }}
                      placeholder="0,00"
                      className="w-full"
                    />
                  </div>

                  <div className="rounded-lg border border-dashed border-[#c4ab83]/40 bg-[#c4ab83]/5 p-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-800">
                      Pagamentos já realizados (opcional)
                    </p>
                    <p className="text-xs text-muted-foreground -mt-2">
                      Marque o que já entrou para abater do total em aberto (vale para valor total ou valor por parcela).
                    </p>
                    {(() => {
                      const dp =
                        parseFloat(
                          formData.down_payment.replace(/\./g, "").replace(",", ".")
                        ) || 0;
                      if (dp <= 0) return null;
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="entrada-ja-paga"
                              checked={prepaid.entradaPaga}
                              onCheckedChange={(c) =>
                                setPrepaid((p) => ({
                                  ...p,
                                  entradaPaga: !!c,
                                  entradaPagaEm: c ? p.entradaPagaEm : "",
                                }))
                              }
                            />
                            <Label htmlFor="entrada-ja-paga" className="text-sm font-normal cursor-pointer">
                              Entrada já recebida
                            </Label>
                          </div>
                          {prepaid.entradaPaga && (
                            <div className="space-y-1 pl-6">
                              <Label className="text-xs text-gray-600">Data do recebimento da entrada</Label>
                              <Input
                                type="date"
                                value={prepaid.entradaPagaEm}
                                onChange={(e) =>
                                  setPrepaid((p) => ({ ...p, entradaPagaEm: e.target.value }))
                                }
                                className="w-full max-w-[220px]"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div className="space-y-2">
                      <Label htmlFor="parcelas-pagas-qty" className="text-sm font-semibold text-gray-800">
                        Parcelas já pagas (quantas)
                      </Label>
                      <Input
                        id="parcelas-pagas-qty"
                        type="number"
                        min={0}
                        max={Math.max(
                          1,
                          parseInt(String(formData.installments_count), 10) || 1
                        )}
                        value={prepaid.parcelasPagasCount}
                        onChange={(e) => {
                          const maxN = Math.max(
                            1,
                            parseInt(String(formData.installments_count), 10) || 1
                          );
                          const raw = parseInt(e.target.value, 10);
                          const n = Number.isFinite(raw)
                            ? Math.min(maxN, Math.max(0, raw))
                            : 0;
                          setPrepaid((p) => ({
                            ...p,
                            parcelasPagasCount: n,
                            parcelasPagasDatas: Array.from({ length: n }, (_, i) => p.parcelasPagasDatas[i] || ""),
                          }));
                        }}
                        className="w-full max-w-[120px]"
                      />
                    </div>

                    {prepaid.parcelasPagasCount > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700">
                          Data de cada parcela já paga (1ª → última quitada)
                        </p>
                        {Array.from({ length: prepaid.parcelasPagasCount }, (_, idx) => (
                          <div key={idx} className="flex flex-col sm:flex-row sm:items-end gap-2">
                            <Label className="text-xs text-gray-600 shrink-0 pt-2">
                              {idx + 1}ª parcela paga em
                            </Label>
                            <Input
                              type="date"
                              value={prepaid.parcelasPagasDatas[idx] || ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPrepaid((p) => {
                                  const next = [...p.parcelasPagasDatas];
                                  next[idx] = v;
                                  return { ...p, parcelasPagasDatas: next };
                                });
                              }}
                              className="w-full max-w-[220px]"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Resumo Financeiro */}
                  <div className="bg-gradient-to-r from-[#c4ab83]/10 to-[#c4ab83]/5 p-4 rounded-lg border border-[#c4ab83]/20">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#c4ab83]" />
                      Resumo Financeiro
                    </h4>
                    {(() => {
                      const summary = calculateInstallmentSummary();
                      const freqLabel = INSTALLMENT_FREQUENCY_OPTIONS.find(
                        (o) => o.value === formData.installment_frequency
                      )?.label;
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">Valor Total</div>
                            <div className="font-semibold text-gray-800">
                              {summary.totalAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">Entrada</div>
                            <div className="font-semibold text-green-600">
                              {summary.downPayment.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">Soma das parcelas</div>
                            <div className="font-semibold text-blue-600">
                              {summary.remainingAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">
                              Parcela ({summary.installmentsCount}x{freqLabel ? ` · ${freqLabel}` : ''})
                            </div>
                            <div className="font-semibold text-[#c4ab83]">
                              {summary.installmentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">Já recebido</div>
                            <div className="font-semibold text-emerald-700">
                              {summary.alreadyPaidAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600 text-xs">Em aberto</div>
                            <div className="font-semibold text-orange-700">
                              {summary.balanceRemaining.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Cronograma de Pagamentos */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs text-gray-600 mb-2">Cronograma (previsto):</div>
                      <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                        {(() => {
                          const summary = calculateInstallmentSummary();
                          const installments = [];
                          const freq = formData.installment_frequency || "mensal";
                          const today = new Date();
                          const startLocal = formData.payment_date
                            ? parseLocalYmdToDate(formData.payment_date) ??
                              new Date(today.getFullYear(), today.getMonth(), today.getDate())
                            : new Date(today.getFullYear(), today.getMonth(), today.getDate());

                          const fmtDay = (d: Date) => format(d, "dd/MM/yyyy", { locale: ptBR });

                          if (summary.downPayment > 0) {
                            const entradaDt = prepaid.entradaPaga
                              ? parseLocalYmdToDate(prepaid.entradaPagaEm)
                              : null;
                            installments.push(
                              <div key="down" className="flex justify-between text-xs gap-2 flex-wrap">
                                <span className="text-green-600 shrink-0">
                                  Entrada
                                  {prepaid.entradaPaga ? " · recebida" : ""}
                                </span>
                                <span className="text-gray-500 text-right">
                                  {entradaDt
                                    ? fmtDay(entradaDt)
                                    : fmtDay(startLocal)}
                                </span>
                                <span className="font-medium shrink-0">
                                  {summary.downPayment.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                </span>
                              </div>
                            );
                          }

                          const k = summary.prepaidParcelCount;
                          for (let i = 1; i <= summary.installmentsCount; i++) {
                            const idx = i - 1;
                            const parcelDate = computeInstallmentLocalDate(startLocal, idx, freq);
                            const isPaid = idx < k;
                            const paidD =
                              isPaid && prepaid.parcelasPagasDatas[idx]
                                ? parseLocalYmdToDate(prepaid.parcelasPagasDatas[idx])
                                : null;
                            installments.push(
                              <div key={i} className="flex justify-between text-xs gap-2 flex-wrap">
                                <span className={`shrink-0 ${isPaid ? "text-emerald-700 font-medium" : "text-gray-600"}`}>
                                  {i}ª parcela{isPaid ? " · paga" : ""}
                                </span>
                                <span className="text-gray-500 text-right">
                                  {paidD ? fmtDay(paidD) : fmtDay(parcelDate)}
                                </span>
                                <span className="font-medium shrink-0">
                                  {summary.installmentValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                </span>
                              </div>
                            );
                          }

                          return installments;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="payment_notes" className="text-sm font-semibold text-gray-800">
                  Observações
                </Label>
                <Textarea
                  id="payment_notes"
                  value={formData.payment_notes}
                  onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })}
                  placeholder="Observações sobre condições, atrasos, reagendamentos, etc."
                  rows={3}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
              <Zap className="w-4 h-4 mr-2" />
              {editingEntry ? 'Atualizar' : 'Criar'} Entrada
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SmartFinancialEntryForm;
